/**
 * Quotation Service for R-Server Integration
 * 
 * Handles quotation calculations via the R-Server climate-testing API.
 * Implements job creation, polling, and result processing.
 */

import { QUOTATION_POLLING_CONFIG } from '../config/quotation-polling';
import { apiClient } from './api';

// R-Server response interfaces
interface RServerResponse {
  job_id: string;
  status: string;
  output?: {
    quotation_result?: FarmerResult[];
    metadata?: {
      calculation_timestamp?: string;
      quotation_script?: string;
      farmers_count?: number;
      git_commit?: string;
      coverage_period?: Record<string, unknown>;
      [key: string]: unknown;
    };
    processing_time_seconds?: number;
  };
  input?: {
    quotation_script?: string;
    num_farmers?: number;
    [key: string]: unknown;
  };
  timestamp?: string;
  [key: string]: unknown;
}

// API response wrappers
interface ApiJobResponse {
  jobId?: string;
  status?: string;
  data?: {
    status?: string;
    error?: string;
    [key: string]: unknown;
  };
  error?: string;
  [key: string]: unknown;
}

interface ApiSubmissionResponse {
  jobId?: string;
  status?: string;
  [key: string]: unknown;
}

interface FarmerResult {
  data?: ScenarioResult[];
  farmerID?: string;
  latitude?: number;
  longitude?: number;
  [key: string]: unknown;
}

interface ScenarioResult {
  deficit_strike_return_period?: number;
  deficit_exhaust_return_period?: number;
  deficit_strike_threshold?: number;
  deficit_exhaust_threshold?: number;
  deficit_premium?: number;
  total_payable?: number;
  deficit_payment_per_mm?: number;
  ANRP?: number;
  GRP?: number;
  tax_amount?: number;
  subsidy_amount?: number;
  [key: string]: unknown;
}

export interface QuotationRequest {
  quoteId: string;
  // Backend builds the full request from quote data, so only quoteId is needed
}

export interface QuotationJob {
  jobId: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  estimatedDuration?: number;
  startedAt: Date;
  completedAt?: Date;
  error?: string;
}

// Raw R-Server scenario data (preserve exactly as received)
export interface RawScenarioData {
  // Deficit peril data
  deficit_strike_return_period?: number;
  deficit_exhaust_return_period?: number;
  deficit_strike_threshold?: number;
  deficit_exhaust_threshold?: number;
  deficit_premium?: number;
  deficit_payment_per_mm?: number;
  
  // Excess peril data (future support)
  excess_strike_return_period?: number;
  excess_exhaust_return_period?: number;
  excess_strike_threshold?: number;
  excess_exhaust_threshold?: number;
  excess_premium?: number;
  excess_payment_per_mm?: number;
  
  // Shared values
  total_payable?: number;
  tax_amount?: number;
  subsidy_amount?: number;
  ANRP?: number;
  GRP?: number;
  NRP?: number;
  
  // Allow for additional peril types
  [key: string]: unknown;
}

export interface QuotationResult {
  jobId: string;
  rawData: {
    farmers: Array<{
      farmerId: string;
      latitude: number;
      longitude: number;
      scenarios: RawScenarioData[];
    }>;
    metadata: {
      calculationDate: string;
      quotationScript?: string;
      processingTimeSeconds?: number;
      farmersCount?: number;
      gitCommit?: string;
      location: {
        latitude: number;
        longitude: number;
      };
      coveragePeriod: {
        startDate: string;
        endDate: string;
      };
    };
  };
  fullResponse?: RServerResponse;
}

export interface QuotationProgress {
  stage: 'validating' | 'submitting' | 'calculating' | 'processing' | 'completed' | 'error';
  message: string;
  progress: number; // 0-100
  estimatedTimeRemaining?: number; // seconds
}

class QuotationService {
  constructor() {
    // Backend proxy handles R-Server communication
  }

  /**
   * Validate if a quotation request is supported by the R-Server
   */
  validateRequest(request: QuotationRequest): { valid: boolean; errors: string[] } {
    const errors: string[] = [];

    // For simplified request format, only validate quoteId
    if (!request.quoteId) {
      errors.push('Quote ID is required');
    }

    // Backend will handle all other validation based on quote data
    return {
      valid: errors.length === 0,
      errors
    };
  }

  /**
   * Submit quotation request via backend proxy
   */
  async submitQuotationRequest(request: QuotationRequest): Promise<QuotationJob> {
    // Validate request
    const validation = this.validateRequest(request);
    if (!validation.valid) {
      throw new Error(`Quotation request validation failed: ${validation.errors.join(', ')}`);
    }

    try {
      const result = await apiClient.submitQuotationRequest(request as unknown as Record<string, unknown>);

      const apiResult = result as ApiSubmissionResponse;
      return {
        jobId: apiResult.jobId || '',
        status: (apiResult.status || 'pending') as 'running' | 'pending' | 'failed' | 'completed',
        estimatedDuration: result.estimatedDuration || 30,
        startedAt: new Date()
      };
    } catch (error) {
      throw new Error(`Failed to submit quotation request: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  /**
   * Poll job status until completion
   */
  async pollJobStatus(jobId: string, onProgress?: (progress: QuotationProgress) => void): Promise<QuotationResult> {
    let attempts = 0;
    const maxAttempts = QUOTATION_POLLING_CONFIG.maxPollingAttempts;
    
    while (attempts < maxAttempts) {
      try {
        const jobStatusData = await apiClient.getQuotationJobStatus(jobId);
        
        // Extract actual status from API response wrapper
        const apiJobData = jobStatusData as ApiJobResponse;
        const actualStatus = apiJobData.data?.status || apiJobData.status;
        
        // Debug logging for failed jobs
        if (actualStatus === 'failed' || actualStatus === 'error' || actualStatus === 'cancelled') {
          console.error('🔴 QUOTATION JOB FAILED:', {
            jobId,
            status: actualStatus,
            error: apiJobData.data?.error || apiJobData.error,
            fullResponse: apiJobData
          });
        }
        
        // Calculate progress
        const progress = Math.min((attempts / maxAttempts) * 100, 95);
        const estimatedTimeRemaining = Math.max(0, (maxAttempts - attempts) * (QUOTATION_POLLING_CONFIG.pollingInterval / 1000));

        // Update progress (but not if job has failed)
        if (onProgress && actualStatus !== 'failed' && actualStatus !== 'error' && actualStatus !== 'cancelled') {
          onProgress({
            stage: actualStatus === 'completed' ? 'processing' : 'calculating',
            message: actualStatus === 'completed' 
              ? 'Processing results...' 
              : `Calculation in progress, results expected in about ${Math.ceil(estimatedTimeRemaining)} seconds`,
            progress,
            estimatedTimeRemaining
          });
        }

        // Check if job is completed
        if (actualStatus === 'completed') {
          // Fetch results via apiClient
          const resultsData = await apiClient.getQuotationResults(jobId);
          
          if (onProgress) {
            onProgress({
              stage: 'completed',
              message: 'Quotation calculation completed successfully',
              progress: 100
            });
          }

          // Extract and preserve raw R-Server data structure
          const rServerData = resultsData as RServerResponse;
          const quotationResults = rServerData.output?.quotation_result || [];
          
          // Preserve raw data exactly as received from R-Server
          const rawFarmersData = quotationResults.map((farmer: FarmerResult) => ({
            farmerId: farmer.farmerID || '',
            latitude: farmer.latitude || 0,
            longitude: farmer.longitude || 0,
            scenarios: farmer.data || [] // Preserve raw scenario data with all peril-specific fields
          }));

          return {
            jobId,
            rawData: {
              farmers: rawFarmersData,
              metadata: {
                calculationDate: rServerData.output?.metadata?.calculation_timestamp || new Date().toISOString(),
                quotationScript: rServerData.output?.metadata?.quotation_script || rServerData.input?.quotation_script,
                processingTimeSeconds: rServerData.output?.processing_time_seconds || 0,
                farmersCount: rServerData.output?.metadata?.farmers_count || rServerData.input?.num_farmers || 1,
                gitCommit: rServerData.output?.metadata?.git_commit,
                location: { 
                  latitude: quotationResults[0]?.latitude || 0,
                  longitude: quotationResults[0]?.longitude || 0
                },
                coveragePeriod: {
                  startDate: (rServerData.output?.metadata?.coverage_period as Record<string, unknown>)?.startDate as string || new Date().toISOString(),
                  endDate: (rServerData.output?.metadata?.coverage_period as Record<string, unknown>)?.endDate as string || new Date().toISOString()
                }
              }
            },
            fullResponse: rServerData
          };
        }

        // Check if job failed (check multiple possible failure statuses)
        if (actualStatus === 'failed' || actualStatus === 'error' || actualStatus === 'cancelled') {
          // Extract error from multiple possible locations
          let errorMessage = 'Unknown error';
          
          // Check various error fields in order of preference
          const errorSources = [
            apiJobData.data?.error_details,
            apiJobData.data?.error_message,
            apiJobData.data?.error,
            apiJobData.data?.message,
            apiJobData.error_details,
            apiJobData.error_message,
            apiJobData.error,
            apiJobData.message
          ];
          
          for (const source of errorSources) {
            if (source && typeof source === 'string' && source !== '') {
              errorMessage = source;
              break;
            } else if (source && typeof source === 'object') {
              // If it's an object, try to stringify it
              errorMessage = JSON.stringify(source);
              break;
            }
          }
          
          // Try to parse and format the error message if it's JSON
          try {
            const parsed = JSON.parse(errorMessage);
            if (parsed.error) {
              // Extract the main error message
              errorMessage = parsed.error;
            } else if (typeof parsed === 'object') {
              // If it's an object with multiple fields, create a formatted message
              errorMessage = Object.entries(parsed)
                .map(([key, value]) => `${key}: ${value}`)
                .join('\n');
            }
          } catch {
            // Not JSON, keep as is
          }
          
          // Unescape special characters like \n, \t, etc. AFTER extracting from JSON
          // This handles cases where the error string itself contains escaped characters
          if (typeof errorMessage === 'string') {
            errorMessage = errorMessage
              .replace(/\\n/g, '\n')
              .replace(/\\t/g, '\t')
              .replace(/\\r/g, '\r')
              .replace(/\\"/g, '"')
              .replace(/\\\\/g, '\\');
          }
          
          // Update progress to show error state before throwing
          if (onProgress) {
            onProgress({
              stage: 'error',
              message: `Quotation calculation failed: ${errorMessage}`,
              progress: 0
            });
          }
          
          // Throw error with a special marker so catch block doesn't retry
          const error = new Error(`Quotation calculation failed: ${errorMessage}`);
          (error as Error & { isJobFailure?: boolean }).isJobFailure = true;
          throw error;
        }

        // Wait before next poll
        await new Promise(resolve => setTimeout(resolve, QUOTATION_POLLING_CONFIG.pollingInterval));
        attempts++;

      } catch (error) {
        // If this is a job failure (not a network error), stop polling immediately
        if ((error as Error & { isJobFailure?: boolean }).isJobFailure) {
          throw error;
        }
        
        // For network/API errors, retry if we haven't exceeded max attempts
        if (attempts >= maxAttempts - 1) {
          throw new Error(`Quotation polling failed after ${maxAttempts} attempts: ${error instanceof Error ? error.message : String(error)}`);
        }
        
        // Wait before retry
        await new Promise(resolve => setTimeout(resolve, QUOTATION_POLLING_CONFIG.pollingInterval));
        attempts++;
      }
    }

    const timeoutSeconds = maxAttempts * (QUOTATION_POLLING_CONFIG.pollingInterval / 1000);
    throw new Error(`Quotation calculation timed out after ${timeoutSeconds} seconds`);
  }

  /**
   * Complete quotation flow: submit request and poll until completion
   */
  async getQuotation(request: QuotationRequest, onProgress?: (progress: QuotationProgress) => void): Promise<QuotationResult> {
    try {
      // Stage 1: Validation
      if (onProgress) {
        onProgress({
          stage: 'validating',
          message: 'Validating quotation request...',
          progress: 0
        });
      }

      // Stage 2: Submission
      if (onProgress) {
        onProgress({
          stage: 'submitting',
          message: 'Submitting request to R-Server...',
          progress: 10
        });
      }

      const job = await this.submitQuotationRequest(request);

      // Stage 3: Polling and calculation
      if (onProgress) {
        onProgress({
          stage: 'calculating',
          message: `Calculation started, results expected in about ${job.estimatedDuration || 30} seconds`,
          progress: 20,
          estimatedTimeRemaining: job.estimatedDuration
        });
      }

      const result = await this.pollJobStatus(job.jobId, onProgress);
      
      return result;

    } catch (error) {
      if (onProgress) {
        onProgress({
          stage: 'error',
          message: `Quotation failed: ${error instanceof Error ? error.message : String(error)}`,
          progress: 0
        });
      }
      throw error;
    }
  }
}

// Export singleton instance
export const quotationService = new QuotationService();

/**
 * Get available quotation scripts from R-Server
 */
export async function getAvailableQuotationScripts(): Promise<string[]> {
  try {
    const response = await fetch('/api/quotation/scripts');
    
    if (!response.ok) {
      throw new Error(`Failed to fetch quotation scripts: ${response.status} ${response.statusText}`);
    }
    
    const result = await response.json();
    
    // Handle nested response structure: {success: true, data: {success: true, data: [...]}}
    if (result.success && result.data) {
      if (Array.isArray(result.data)) {
        return result.data.map((script: { name: string; version: string }) => `${script.name}@${script.version}`);
      } else if (result.data.success && Array.isArray(result.data.data)) {
        return result.data.data.map((script: { name: string; version: string }) => `${script.name}@${script.version}`);
      }
    } else if (Array.isArray(result)) {
      return result;
    } else {
      console.warn('Unexpected quotation scripts API response format:', result);
      return ['maize_quotation@1.0.0']; // Fallback
    }
  } catch (error) {
    console.error('Error fetching quotation scripts:', error);
    return ['maize_quotation@1.0.0']; // Fallback to default script
  }
  
  return ['maize_quotation@1.0.0']; // Final fallback
}

/**
 * Get available claims scripts from R-Server
 */
export async function getAvailableClaimsScripts(): Promise<string[]> {
  const fallback = ['maize_claims@1.0.0', 'maize_claims@1.1.0'];
  try {
    const response = await fetch('/api/claims/scripts');

    if (!response.ok) {
      console.warn(`Claims scripts endpoint returned ${response.status}, using fallback`);
      return fallback;
    }

    const result = await response.json();

    if (result.success && result.data) {
      if (Array.isArray(result.data)) {
        return result.data.map((script: string | { name: string; version: string }) =>
          typeof script === 'string' ? script : `${script.name}@${script.version}`
        );
      } else if (result.data.success && Array.isArray(result.data.data)) {
        return result.data.data.map((script: { name: string; version: string }) => `${script.name}@${script.version}`);
      }
    } else if (Array.isArray(result)) {
      return result;
    }
    console.warn('Unexpected claims scripts API response format:', result);
    return fallback;
  } catch (error) {
    console.error('Error fetching claims scripts:', error);
    return fallback;
  }
}
